import { ButtonHTMLAttributes, ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import { Icon } from '../assets/icons/Icon';
import { cn } from '../utils/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'plain';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
  className?: string;
  variant?: ButtonVariant;
  isLoading?: boolean;
};
type ButtonComponentProps<T extends ElementType> = ButtonProps & {
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof ButtonProps | 'as'>;

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'rm-button primary',
  secondary: 'rm-button secondary',
  danger: 'rm-button danger',
  plain: 'rm-button plain',
};

export const Button = <T extends ElementType = 'button'>({
  as,
  children,
  ariaLabel = '',
  variant = 'primary',
  isLoading,
  className = '',
  disabled,
  type,
  onClick,
  ...props
}: ButtonComponentProps<T>) => {
  // Cast to any: JSX distributes prop resolution over the ElementType union and
  // collapses to never under React 19 types. The `as` prop is still type-checked
  // at the call site via the T generic.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component: any = as || 'button';

  const typeProp =
    Component === 'button' ? { type: type ?? 'button', disabled, onClick } : { onClick };

  return (
    <Component
      aria-label={ariaLabel}
      className={cn(className, buttonVariants[variant], 'focus-style')}
      {...typeProp}
      {...props}
    >
      {isLoading ? <Icon iconName="spinner" /> : children}
    </Component>
  );
};
